import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type PortalType = 'Crew' | 'Manager';

export interface WhitelistItem {
  id: number;
  name: string;
  url: string;
  role: PortalType;
  category: string;
  displayOrder: number;
  isActive: boolean;
  documentId: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class WhitelistService {

  private readonly apiUrl =
    `${environment.apiUrl}/whitelists`;

  constructor(private http: HttpClient) {}

  // =========================================================
  // PORTAL READS
  // =========================================================

  getForPortal(
    portal: PortalType
  ): Observable<WhitelistItem[]> {

    return this.http.get<WhitelistItem[]>(
      `${this.apiUrl}/${portal.toLowerCase()}`,
      {
        withCredentials: true
      }
    );
  }

  // =========================================================
  // ADMIN
  // =========================================================

  getAll(): Observable<WhitelistItem[]> {
    return this.http.get<WhitelistItem[]>(
      this.apiUrl,
      {
        withCredentials: true
      }
    );
  }

  create(
    item: Omit<WhitelistItem, 'id' | 'documentId'>
  ): Observable<WhitelistItem> {

    return this.http.post<WhitelistItem>(
      this.apiUrl,
      item,
      {
        withCredentials: true
      }
    );
  }

  update(
    item: WhitelistItem
  ): Observable<void> {

    return this.http.put<void>(
      `${this.apiUrl}/${item.id}`,
      item,
      {
        withCredentials: true
      }
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${id}`,
      {
        withCredentials: true
      }
    );
  }

  // =========================================================
  // DOCUMENTS
  // =========================================================

  getDocument(id: number): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/${id}/document`,
      {
        withCredentials: true,
        responseType: 'blob'
      }
    );
  }

  uploadDocument(
    id: number,
    file: File
  ): Observable<{
    documentId: number;
    fileName: string;
  }> {

    const formData = new FormData();

    formData.append('file', file);

    return this.http.post<{
      documentId: number;
      fileName: string;
    }>(
      `${this.apiUrl}/${id}/document`,
      formData,
      {
        withCredentials: true
      }
    );
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${id}/document`,
      {
        withCredentials: true
      }
    );
  }
}